const GOOGLE_ANALYTICS_SCRIPT_ID = 'mall-google-analytics-script'
const MAX_PENDING_EVENT_COUNT = 100

const configuredMeasurementIds = new Set()
const pendingEvents = []

function getWindow() {
  return typeof window !== 'undefined' ? window : null
}

function parseConfigData(configData) {
  if (!configData)
    return {}
  if (typeof configData === 'object' && !Array.isArray(configData))
    return configData
  if (typeof configData !== 'string')
    return {}

  try {
    const parsed = JSON.parse(configData)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  }
  catch {
    return {}
  }
}

function resolveMallSiteConfig(configData) {
  const parsedConfig = parseConfigData(configData)
  const directMallSiteConfig = parseConfigData(parsedConfig.mallSiteConfig)
  if (Object.keys(directMallSiteConfig).length)
    return directMallSiteConfig

  const nestedOtherJson = parseConfigData(parsedConfig.otherJson)
  return parseConfigData(nestedOtherJson.mallSiteConfig)
}

function normalizeMeasurementIds(value) {
  if (!Array.isArray(value))
    return []
  return [...new Set(value
    .map(item => String(item ?? '').trim())
    .filter(Boolean))]
}

export function resolveGoogleAnalyticsMeasurementIds(configData) {
  return normalizeMeasurementIds(resolveMallSiteConfig(configData).googleAnalyticsIds)
}

export function resolveGoogleAnalyticsMeasurementId(configData) {
  return resolveGoogleAnalyticsMeasurementIds(configData)[0] || ''
}

function ensureGoogleAnalyticsQueue(win) {
  win.dataLayer = Array.isArray(win.dataLayer) ? win.dataLayer : []
  if (typeof win.gtag !== 'function') {
    win.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params -- gtag.js requires an Arguments object in dataLayer.
      win.dataLayer.push(arguments)
    }
  }
}

export function initializeGoogleAnalytics(configData) {
  const win = getWindow()
  if (!win?.document)
    return ''

  const measurementIds = resolveGoogleAnalyticsMeasurementIds(configData)
  if (!measurementIds.length)
    return ''

  ensureGoogleAnalyticsQueue(win)

  if (!win.document.getElementById(GOOGLE_ANALYTICS_SCRIPT_ID)) {
    const script = win.document.createElement('script')
    script.id = GOOGLE_ANALYTICS_SCRIPT_ID
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementIds[0])}`
    win.document.head?.appendChild(script)
  }

  if (!configuredMeasurementIds.size)
    win.gtag('js', new Date())

  measurementIds.forEach((measurementId) => {
    if (configuredMeasurementIds.has(measurementId))
      return
    win.gtag('config', measurementId, { send_page_view: false })
    configuredMeasurementIds.add(measurementId)
  })

  flushPendingEvents(win)

  return measurementIds[0]
}

function resolveEventCategory(eventName, params) {
  if (params.category)
    return params.category
  if (eventName === 'app_init')
    return 'App'
  if (eventName.includes('order') || eventName.includes('payment') || eventName.includes('checkout')) {
    return 'Checkout'
  }
  if (eventName.includes('goods') || eventName.includes('cart') || eventName.includes('buy_now')) {
    return 'Product'
  }
  return 'Mall'
}

function resolveEventLabel(params) {
  return (
    params.label
    || params.title
    || params.itemName
    || params.item_name
    || params.orderId
    || params.order_id
    || params.screenName
    || params.screen_name
    || ''
  )
}

function buildGoogleAnalyticsEventParams(eventName, params = {}) {
  const win = getWindow()
  const source = params && typeof params === 'object' && !Array.isArray(params) ? params : {}
  const eventParams = { ...source }
  delete eventParams.action
  delete eventParams.category
  delete eventParams.label
  delete eventParams.pageLocation
  delete eventParams.pagePath
  delete eventParams.pageTitle
  const value = source.value ?? source.price
  const pagePath = source.page_path
    || source.pagePath
    || source.fullPath
    || source.path
    || `${win?.location?.pathname || ''}${win?.location?.search || ''}${win?.location?.hash || ''}`

  return {
    ...eventParams,
    event_category: resolveEventCategory(eventName, source),
    event_label: resolveEventLabel(source),
    page_location: source.page_location || source.pageLocation || win?.location?.href || undefined,
    page_path: pagePath || undefined,
    page_title: source.page_title || source.pageTitle || win?.document?.title || undefined,
    ...(value !== undefined && value !== null && value !== '' ? { value } : {}),
  }
}

function defer(callback) {
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(callback)
    return
  }
  Promise.resolve().then(callback)
}

function dispatchEvent(win, action, eventParams) {
  defer(() => {
    try {
      win.gtag('event', action, eventParams)
    }
    catch {
      // GA is a best-effort side channel and must never interrupt the existing event upload.
    }
  })
}

function queuePendingEvent(action, eventParams) {
  pendingEvents.push({ action, eventParams })
  if (pendingEvents.length > MAX_PENDING_EVENT_COUNT) {
    pendingEvents.splice(0, pendingEvents.length - MAX_PENDING_EVENT_COUNT)
  }
}

function flushPendingEvents(win) {
  if (typeof win.gtag !== 'function' || !pendingEvents.length)
    return
  const events = pendingEvents.splice(0, pendingEvents.length)
  events.forEach(({ action, eventParams }) => dispatchEvent(win, action, eventParams))
}

export function trackGoogleAnalyticsEvent(eventName, params = {}) {
  const win = getWindow()
  const action = String(params?.action || eventName || '').trim()
  if (!win || !action)
    return false

  const eventParams = buildGoogleAnalyticsEventParams(eventName, params)
  if (typeof win.gtag !== 'function') {
    queuePendingEvent(action, eventParams)
    return true
  }
  dispatchEvent(win, action, eventParams)
  return true
}

export function trackGoogleAnalyticsPageView(
  screenName,
  routeParams = {},
  fromRouteName,
  fromRoutePath,
) {
  const win = getWindow()
  if (!win || !screenName)
    return false

  const pagePath = String(routeParams?.fullPath || routeParams?.path || screenName || '')
  let pageReferrer = win.document?.referrer || undefined
  if (fromRoutePath) {
    try {
      pageReferrer = new URL(String(fromRoutePath), win.location?.origin).href
    }
    catch {
      pageReferrer = String(fromRoutePath)
    }
  }

  return trackGoogleAnalyticsEvent('page_view', {
    action: 'page_view',
    screenName,
    fromRouteName,
    page_path: pagePath,
    page_location: win.location?.href,
    page_title: win.document?.title,
    page_referrer: pageReferrer,
  })
}
